import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { resolveCoverUrl } from '../../services/api';
import styles from './ReadingHistoryPage.module.css';
import { LoaderIcon, HistoryIcon, BookIcon } from '../../components/Icons';

const ReadingHistoryPage = () => {
  const navigate = useNavigate();
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/history-chapters/', {
          params: {
            limit: 200,
          },
        });
        setHistoryItems(response.data || []);
      } catch (err) {
        console.error('获取阅读历史失败:', err);
        setError('获取阅读历史失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const handleBack = () => {
    navigate('/profile');
  };

  const handleOpenChapter = (item) => {
    navigate(`/read?bookid=${item.novel_id}&chapterid=${item.chapter_id}`, {
      state: {
        from: '/profile/history',
      },
    });
  };

  const handleToggleGroup = (groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const emptyState = useMemo(() => !loading && !error && historyItems.length === 0, [loading, error, historyItems]);

  const groupedHistory = useMemo(() => {
    if (historyItems.length === 0) return [];

    const groups = [];
    const cache = new Map();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const sorted = [...historyItems]
      .filter((item) => new Date(item.created_at) >= oneMonthAgo)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const formatLabel = (dateObj) => {
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const targetStart = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
      const diff = Math.round((todayStart - targetStart) / (24 * 60 * 60 * 1000));

      if (diff === 0) return '今天';
      if (diff === 1) return '昨天';

      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${month}.${day}`;
    };

    sorted.forEach((item) => {
      const createdAt = new Date(item.created_at);
      const key = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}-${String(createdAt.getDate()).padStart(2, '0')}`;

      if (!cache.has(key)) {
        const group = {
          key,
          label: formatLabel(createdAt),
          items: [],
        };
        cache.set(key, group);
        groups.push(group);
      }

      cache.get(key).items.push(item);
    });

    return groups;
  }, [historyItems]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.stateWrapper}>
          <LoaderIcon size={28} />
          <span>加载阅读历史中...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.stateWrapper}>
          <p className={styles.errorText}>{error}</p>
        </div>
      );
    }

    if (emptyState) {
      return (
        <div className={styles.stateWrapper}>
          <HistoryIcon size={36} />
          <span>暂时没有阅读历史记录</span>
        </div>
      );
    }

    return (
      <div className={styles.historyList}>
        {groupedHistory.map((group) => (
          <div key={group.key} className={styles.historyGroup}>
            <button
              type="button"
              className={styles.groupHeader}
              onClick={() => handleToggleGroup(group.key)}
              aria-expanded={!collapsedGroups.has(group.key)}
            >
              <span className={styles.groupArrow}>
                {collapsedGroups.has(group.key) ? '▸' : '▾'}
              </span>
              {group.label}
            </button>
            {!collapsedGroups.has(group.key) && (
              <div className={styles.groupEntries}>
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={styles.historyItem}
                    onClick={() => handleOpenChapter(item)}
                  >
                    <div className={styles.historyCover}>
                      {item.novel_cover ? (
                        <img
                          src={resolveCoverUrl(item.novel_cover)}
                          alt={item.novel_title || '封面'}
                          className={styles.historyCoverImg}
                          loading="lazy"
                        />
                      ) : (
                        <div className={styles.historyCoverPlaceholder}>
                          <BookIcon size={20} />
                        </div>
                      )}
                    </div>
                    <div className={styles.historyContent}>
                      <h3 className={styles.historyTitle}>{item.chapter_title || '章节'}</h3>
                      <p className={styles.historyMeta}>
                        <span className={styles.metaNovel}>{item.novel_title || '未知书籍'}</span>
                      </p>
                    </div>
                    <span className={styles.historyArrow}>›</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        <p className={styles.historyHint}>仅显示近一个月的阅读记录</p>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <header className={styles.topBar}>
        <button type="button" className={styles.backBtn} onClick={handleBack} aria-label="返回个人中心">
          <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
        </button>
        <h1 className={styles.title}>阅读历史</h1>
      </header>
      <main className={styles.content}>{renderContent()}</main>
    </div>
  );
};

export default ReadingHistoryPage;
